import { useLocalStorage } from 'react-use';
import { type CSSProperties, type ReactNode, useCallback, useMemo, useRef } from 'react';
import { safeSessionStorage } from '@pkgs/components/utils/storageUtils';
import { type PendingUserAccountItem, PendingUserAccountItemType } from '@pkgs/components/types/types';
import { useMapVisibilityStore } from '@pkgs/components/providers/MapVisibilityStoreProvider';
import { type TFunction } from '@pkgs/components/providers/AppContextProvider';
import { useWatchlist } from '@pkgs/components/hooks/useWatchlist';
import { useUserAccount } from '@pkgs/components/hooks/useUserAccount';
import { useAppContext } from '@pkgs/components/hooks/useAppContext';
import { useHoverStore } from '@pkgs/components/hooks/stores/useHoverStore';
import { useSearchStore } from '@pkgs/components/hooks/stores/search/useSearchStore';
import { MAP_VIEW_RETURN_SESSION_KEY, PENDING_USER_ACCOUNT_ITEM_KEY } from '@pkgs/components/consts/consts';
import { getSearchListingResultId, type SearchListingResult } from '@ev-tech/search-modules-api';

import { getSearchResultCardLink } from '../SearchResultCard/SearchResultCard.utils';
import { SearchResultCard, type SearchResultsCardVariant } from '../SearchResultCard/SearchResultCard';

type Props = {
  shouldPreloadImage: boolean;
  variant?: SearchResultsCardVariant;
  className?: string;
  isApproximateLocation?: boolean;
  listingResult: SearchListingResult;
  shouldHideImage?: boolean;
  onClick?: (exposeUrl: string) => void;
  style?: CSSProperties;
  aiBadge?: ReactNode;
};

export const SearchResultCardWrapper = ({
  listingResult,
  shouldPreloadImage,
  variant,
  className,
  isApproximateLocation,
  shouldHideImage = false,
  onClick,
  style,
}: Props) => {
  const {
    t,
    countryCode,
    options: { language, currency, measurementSystem },
    autogenConfig,
    ImageComponent,
    LinkComponent,
  } = useAppContext();
  const { getIsInWatchlist, handleWatchlistClick } = useWatchlist();
  const searchMode = useSearchStore((state) => state.searchMode);
  const isMapToggledOn = useMapVisibilityStore((state) => state.isMapActive);
  const cardElementRef = useRef<HTMLDivElement | null>(null);
  const { openUserAccountModal } = useUserAccount();
  const [, setPendingItemToSave] = useLocalStorage<PendingUserAccountItem | null>(PENDING_USER_ACCOUNT_ITEM_KEY);

  const setHoveredPropertyId = useHoverStore((state) => state.setHoveredPropertyId);

  const handleCardClick = useCallback(
    (exposeUrl: string) => {
      setHoveredPropertyId(undefined);

      // Store map state before navigation if user is currently viewing the map
      if (isMapToggledOn) {
        safeSessionStorage.setItem(MAP_VIEW_RETURN_SESSION_KEY, 'true');
      }

      onClick?.(exposeUrl);
    },
    [setHoveredPropertyId, isMapToggledOn, onClick],
  );

  const handleOpenUserAccountModal = useCallback(() => {
    const exclusiveListing = listingResult.userAccountExclusive;
    if (exclusiveListing) {
      const { href } = getSearchResultCardLink({
        listing: exclusiveListing,
        language,
        countryCode,
      });

      if (isMapToggledOn) {
        safeSessionStorage.setItem(MAP_VIEW_RETURN_SESSION_KEY, 'true');
      }

      setPendingItemToSave({ type: PendingUserAccountItemType.UA_EXCLUSIVE_EXPOSE, uAExclusiveAuthExposeUrl: href });
    }

    openUserAccountModal({ triggerId: 'user-account-exclusive-card_tertiary_sign-in' });
  }, [listingResult.userAccountExclusive, language, countryCode, isMapToggledOn, setPendingItemToSave, openUserAccountModal]);

  const isInWatchlist = useMemo(() => getIsInWatchlist(getSearchListingResultId(listingResult)), [getIsInWatchlist, listingResult]);

  const listingId = getSearchListingResultId(listingResult);

  const handleMouseEnter = useCallback(() => {
    if (variant !== 'preview') setHoveredPropertyId(listingId);
  }, [variant, setHoveredPropertyId, listingId]);

  const handleMouseLeave = useCallback(() => {
    if (variant !== 'preview') setHoveredPropertyId(undefined);
  }, [variant, setHoveredPropertyId]);

  return (
    <SearchResultCard
      className={className}
      key={listingId}
      ref={cardElementRef}
      listingResult={listingResult}
      shouldPreloadImage={shouldPreloadImage}
      isInWatchlist={isInWatchlist}
      onWatchlistClick={handleWatchlistClick}
      imageComponent={ImageComponent}
      linkComponent={LinkComponent}
      currency={currency}
      measurementSystem={measurementSystem}
      language={language}
      translationResolver={t as TFunction}
      aiBadgeTranslationKey="search.label.ai"
      variant={variant}
      countryCode={countryCode}
      onClickPropertyCardLink={handleCardClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      isApproximateLocation={isApproximateLocation}
      searchMode={searchMode}
      shouldHideImage={shouldHideImage}
      carouselPreloadIndex={autogenConfig ? 1 : 2}
      style={style}
      openUserAccountModal={handleOpenUserAccountModal}
    />
  );
};
